import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group"
import { cn } from "../lib/utils"

export function ToggleGroupRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const atomDefaults = useAtomDefaults("toggle-group")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const [selectedValue, setSelectedValue] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    "",
    String,
  )

  const items = element.children ?? []

  return (
    <ToggleGroup
      value={[selectedValue]}
      onValueChange={(newValue: unknown) => {
        // base-ui ToggleGroup returns an array of pressed values
        const arr = newValue as string[]
        const val = arr[arr.length - 1] ?? ""
        setSelectedValue(val)
        dispatchEvent(element.id, "change", { value: val })
      }}
      className={cn(element.className)}
      data-testid={`toggle-group-${element.id}`}
      data-atom-type="toggle-group"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      aria-label={element.ariaLabel ?? "Toggle group"}
    >
      {items.map((item) => {
        const label = item.text ? String(resolveSymbol(item.text) ?? "") : item.id
        const itemValue = item.text ?? item.id

        return (
          <ToggleGroupItem
            key={item.id}
            value={itemValue}
            aria-label={item.ariaLabel ?? label}
            onClick={() => {
              dispatchEvent(item.id, "tap", {
                value: itemValue,
                selected: true,
              })
            }}
          >
            {label}
          </ToggleGroupItem>
        )
      })}
    </ToggleGroup>
  )
}
